import React from "react";
import {
  Input,
  Select,
  SelectItem,
  Card,
  CardBody,
} from "@heroui/react";
import {
  Briefcase,
  IdentificationCard,
  MapPin,
  Bank,
} from "@phosphor-icons/react";
import CompanyIbanList from "./CompanyIbanList";
import { AddressPicker } from "components/index";

interface CompanyGegevensTabProps {
  company: any;
  onChange: (field: string, value: any) => void;
  onAddressChange?: (description: string, placeId: string) => void;
  isDisabled?: boolean;
}

const LEGAL_FORM_OPTIONS = [
  { key: "NV", label: "NV" },
  { key: "BVBA", label: "BVBA" },
  { key: "BV", label: "BV" },
];

const INPUT_CLASSNAMES = {
  label: "text-xs font-medium",
  input: "text-sm",
};

const SELECT_CLASSNAMES = {
  label: "text-xs font-medium",
  value: "text-sm",
};

const CARD_HEADER_CLASSNAME = "flex items-center gap-2";
const ICON_CLASSNAME = "h-4 w-4 text-moss-primary";
const CARD_TITLE_CLASSNAME = "text-sm font-semibold text-neutral-primary";

const CompanyGegevensTab: React.FC<CompanyGegevensTabProps> = ({
  company,
  onChange,
  onAddressChange,
  isDisabled = false,
}) => {
  return (
    <div className="flex flex-col gap-3 py-3">
      {/* Basic Information Card */}
      <Card shadow="none" className="border border-gray-200">
        <CardBody className="gap-3 p-3">
          <div className={CARD_HEADER_CLASSNAME}>
            <Briefcase className={ICON_CLASSNAME} weight="duotone" />
            <h3 className={CARD_TITLE_CLASSNAME}>Basisinformatie</h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Bedrijfsnaam"
              size="sm"
              value={company?.name || ""}
              onChange={(e) => onChange("name", e.target.value)}
              variant="bordered"
              isDisabled={isDisabled}
              classNames={INPUT_CLASSNAMES}
            />
            <Select
              label="Rechtsvorm"
              size="sm"
              selectedKeys={company?.legalForm ? [company.legalForm] : []}
              onChange={(e) => onChange("legalForm", e.target.value)}
              variant="bordered"
              isDisabled={isDisabled}
              classNames={SELECT_CLASSNAMES}
            >
              {LEGAL_FORM_OPTIONS.map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>
          </div>
        </CardBody>
      </Card>

      {/* Registration Numbers Card */}
      <Card shadow="none" className="border border-gray-200">
        <CardBody className="gap-3 p-3">
          <div className={CARD_HEADER_CLASSNAME}>
            <IdentificationCard className={ICON_CLASSNAME} weight="duotone" />
            <h3 className={CARD_TITLE_CLASSNAME}>Registratienummers</h3>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <Input
              label="KBO nummer"
              size="sm"
              value={company?.kbo || ""}
              onChange={(e) => onChange("kbo", e.target.value)}
              variant="bordered"
              isDisabled={isDisabled}
              classNames={INPUT_CLASSNAMES}
            />
            <Input
              label="BTW nummer"
              size="sm"
              value={company?.vat || ""}
              onChange={(e) => onChange("vat", e.target.value)}
              variant="bordered"
              isDisabled={isDisabled}
              classNames={INPUT_CLASSNAMES}
            />
            <Input
              label="Nummer professionele verkoper"
              size="sm"
              value={company?.numberProfessionalSeller || ""}
              onChange={(e) => onChange("numberProfessionalSeller", e.target.value)}
              variant="bordered"
              isDisabled={isDisabled}
              classNames={INPUT_CLASSNAMES}
            />
            <Input
              label="Odoo ID"
              size="sm"
              isDisabled={isDisabled}
              value={company?.odooId?.toString() || ""}
              onChange={(e) =>
                onChange("odooId", e.target.value ? parseInt(e.target.value) : null)
              }
              type="number"
              variant="bordered"
              isReadOnly
              classNames={{
                label: "text-xs font-medium",
                input: "text-sm bg-gray-50",
              }}
            />
          </div>
        </CardBody>
      </Card>

      {/* Banking Card */}
      <Card shadow="none" className="border border-gray-200">
        <CardBody className="gap-3 p-3">
          <div className={CARD_HEADER_CLASSNAME}>
            <Bank className={ICON_CLASSNAME} weight="duotone" />
            <h3 className={CARD_TITLE_CLASSNAME}>Bankgegevens</h3>
          </div>

          <CompanyIbanList
            ibans={company?.companyIbans || []}
            onChange={(ibans) => onChange("companyIbans", ibans)}
            isDisabled={isDisabled}
          />
        </CardBody>
      </Card>

      {/* Location Card */}
      <Card shadow="none" className="border border-gray-200">
        <CardBody className="gap-3 p-3">
          <div className={CARD_HEADER_CLASSNAME}>
            <MapPin className={ICON_CLASSNAME} weight="duotone" />
            <h3 className={CARD_TITLE_CLASSNAME}>Locatie</h3>
          </div>

          <div className={isDisabled ? "pointer-events-none" : ""}>
            <AddressPicker
              editMode={!isDisabled}
              defaultAddress={company?.googlePlaceLongAddress || ""}
              getAddress={(selectedPlace) => {
                onChange("googlePlaceLongAddress", selectedPlace.description);
                onChange("googlePlaceId", selectedPlace.placeId);
                onAddressChange?.(selectedPlace.description, selectedPlace.placeId);
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CompanyGegevensTab;